// ============================================================================
// FILE: frontend/src/modules/ipe/engineering/material_operation/BOMOperationMappingView.js
// MODULE: IPE → Engineering → BOM Operation Mapping
//
// PURPOSE
// -----------------------------------------------------------------------------
// Matrix-based UI to map Engineering BOM materials to MOM operations.
//
// UI PATTERN
// -----------------------------------------------------------------------------
// Rows    → BOM Materials (eng_bom_line)
// Columns → MOM Operations (eng_mom_line)
// Cells   → Assigned quantity per operation
//
// FEATURES
// -----------------------------------------------------------------------------
// ✔ Excel-style keyboard navigation
// ✔ Immutable React state updates
// ✔ Allocation validation (cannot exceed BOM qty)
// ✔ Sticky header + scrollable grid
// ✔ Batch save payload
// ✔ Compatible with TaskFlow axiosClient service layer
// ✔ Frontend validation (row highlight + save disable)
// ============================================================================

import React, { useEffect, useState, useMemo, useRef } from "react";
import axiosClient from "../../../../api/axiosClient";

const BOMOperationMappingView = ({ enquiryItemId }) => {

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [mapping, setMapping] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [momId, setMomId] = useState(null);

  // Excel-like navigation
  const inputRefs = useRef({});

  // ---------------------------------------------------------------------------
  // HELPER — CALCULATE MATERIAL TOTAL
  // ---------------------------------------------------------------------------
  const calculateTotal = (material) => {
    return operations.reduce((sum, op) => {
      const key = `${material.bom_line_id}_${op.id}`;
      return sum + (Number(mapping?.[op.id]?.[material.bom_line_id]) || 0);
    }, 0);
  };

  // ---------------------------------------------------------------------------
  // LOAD DATA
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!enquiryItemId) {
      setLoading(false);
      return;
    }

    loadMappingData();
  }, [enquiryItemId]);

  const loadMappingData = async () => {

    try {

      setLoading(true);

      const momRes = await axiosClient.get(
        `/api/ipe/moms/enquiry-items/${enquiryItemId}/mom`
      );

      const momId = momRes.data?.data?.id;

      if (!momId) {
        setLoading(false);
        return;
      }

      setMomId(momId);

      const res = await axiosClient.get(
        `/api/ipe/bom-operation-mappings/matrix/${momId}`
      );

      const data = res.data?.data || {};

      setMaterials(data.materials || []);
      setOperations(data.operations || []);

      const matrix = {};

      (data.mappings || []).forEach(m => {

        if (!matrix[m.mom_line_id]) {
          matrix[m.mom_line_id] = {};
        }

        matrix[m.mom_line_id][m.bom_line_id] = m.assigned_quantity;

      });

      setMapping(matrix);

    } catch (err) {

      console.error(err);
      setError("Failed to load BOM operation mapping");

    } finally {

      setLoading(false);

    }
  };

  // ---------------------------------------------------------------------------
  // UPDATE QUANTITY (IMMUTABLE STATE UPDATE)
  // ---------------------------------------------------------------------------
  const updateQuantity = (operationId, bomLineId, value) => {

    const qty = parseFloat(value || 0);

    setMapping(prev => ({
      ...prev,
      [operationId]: {
        ...(prev[operationId] || {}),
        [bomLineId]: qty
      }
    }));

  };

  // ---------------------------------------------------------------------------
  // EXCEL NAVIGATION
  // ---------------------------------------------------------------------------
  const registerInputRef = (r, c, el) => {

    if (!inputRefs.current[r])
      inputRefs.current[r] = {};

    inputRefs.current[r][c] = el;

  };

  const moveFocus = (r, c) => {

    const el = inputRefs.current?.[r]?.[c];

    if (el) el.focus();

  };

  const handleKeyNavigation = (e, r, c) => {

    switch (e.key) {

      case "Enter":
      case "ArrowRight":
        e.preventDefault();
        moveFocus(r, c + 1);
        break;

      case "ArrowLeft":
        e.preventDefault();
        moveFocus(r, c - 1);
        break;

      case "ArrowDown":
        e.preventDefault();
        moveFocus(r + 1, c);
        break;

      case "ArrowUp":
        e.preventDefault();
        moveFocus(r - 1, c);
        break;

      default:
        break;
    }
  };

  // ---------------------------------------------------------------------------
  // MATERIAL TOTAL CALCULATION
  // ---------------------------------------------------------------------------
  const materialTotals = useMemo(() => {

    const totals = {};

    Object.values(mapping).forEach(op => {

      Object.entries(op).forEach(([bomLineId, qty]) => {

        totals[bomLineId] =
          (totals[bomLineId] || 0) + Number(qty || 0);

      });

    });

    return totals;

  }, [mapping]);

  // ---------------------------------------------------------------------------
  // VALIDATION
  // ---------------------------------------------------------------------------
  const isValidAllocation = () =>
    materials.every(mat =>
      (materialTotals[mat.bom_line_id] || 0) <= mat.quantity
    );

  // ---------------------------------------------------------------------------
  // SAVE MAPPING
  // ---------------------------------------------------------------------------
  const handleSave = async () => {

    if (!isValidAllocation()) {

      alert("Allocated quantity exceeds BOM quantity.");

      return;
    }

    try {

      setSaving(true);

      const payload = [];

      Object.entries(mapping).forEach(([operationId, mats]) => {

        Object.entries(mats).forEach(([bomLineId, qty]) => {

          if (qty > 0) {

            payload.push({
              mom_line_id: Number(operationId),
              bom_line_id: Number(bomLineId),
              assigned_quantity: qty
            });

          }

        });

      });

      await axiosClient.post(
        `/api/ipe/bom-operation-mappings/`,
        {
          mom_id: momId,
          mappings: payload
        }
      );

      alert("Mapping saved successfully");

    } catch {

      alert("Failed to save mapping");

    } finally {

      setSaving(false);

    }
  };

  const getQty = (operationId, bomLineId) =>
    mapping?.[operationId]?.[bomLineId] ?? "";

  // ---------------------------------------------------------------------------
  // UI STATES
  // ---------------------------------------------------------------------------
  if (loading)
    return <div className="p-4">Loading mapping...</div>;

  if (error)
    return <div className="p-4 text-danger">{error}</div>;

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (

    <div className="card shadow-sm">

      <div className="card-header d-flex justify-content-between align-items-center">

        <h5 className="mb-0">
          BOM Operation Material Mapping
        </h5>

        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !isValidAllocation()}
        >
          {saving ? "Saving..." : "Save Mapping"}
        </button>

      </div>

      <div
        className="card-body table-responsive"
        style={{ maxHeight: "65vh" }}
      >

        <table className="table table-bordered align-middle">

          <thead className="table-light sticky-top">

            <tr>

              <th style={{ minWidth: 260 }}>
                Material
              </th>

              <th>
                BOM Qty
              </th>

              {operations.map(op => (

                <th
                  key={op.id}
                  className="text-center"
                >

                  {op.sequence_no}

                  <div className="small text-muted">
                    {op.operation_name}
                  </div>

                </th>

              ))}

              <th>Total</th>

            </tr>

          </thead>

          <tbody>

            {materials.map((mat, rowIndex) => {

              const total =
                materialTotals[mat.bom_line_id] || 0;

              const exceeded =
                total > mat.quantity;

              return (

                <tr
                  key={mat.bom_line_id}
                  style={{
                    background: exceeded ? "#ffe6e6" : "transparent"
                  }}
                >

                  <td>
                    <strong>{mat.material_name}</strong>
                  </td>

                  <td>{mat.quantity}</td>

                  {operations.map((op, colIndex) => (

                    <td
                      key={`${mat.bom_line_id}_${op.id}`}
                    >

                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={getQty(
                          op.id,
                          mat.bom_line_id
                        )}

                        ref={(el) =>
                          registerInputRef(
                            rowIndex,
                            colIndex,
                            el
                          )
                        }

                        onKeyDown={(e) =>
                          handleKeyNavigation(
                            e,
                            rowIndex,
                            colIndex
                          )
                        }

                        onChange={(e) =>
                          updateQuantity(
                            op.id,
                            mat.bom_line_id,
                            e.target.value
                          )
                        }

                        min="0"
                        step="0.01"
                      />

                    </td>

                  ))}

                  <td
                    className={
                      exceeded
                        ? "text-danger fw-bold"
                        : "fw-semibold"
                    }
                  >
                    {total}
                  </td>

                </tr>

              );
            })}

          </tbody>

        </table>

      </div>

    </div>
  );
};

export default BOMOperationMappingView;